import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel
} from '../../../../../components/confirm-dialog/confirm-dialog.component';
import {DistributionAccount} from '../../../../../distribution-account/distribution-account.model';
import {NotificationService} from '../../../../../utils/notification-service';
import {SubscriptionHandler} from '../../../../../utils/subscription-handler';
import {XandrService} from '../../../../../xandr/xandr.service';
import {XandrBillingType, XandrPricingDataCategory, XandrSegmentBilling} from '../../../../../xandr/xandr.types';

@Component({
  selector: 'app-xandr-price',
  templateUrl: './xandr-price.component.html',
  styleUrls: ['./xandr-price.component.scss']
})
export class XandrPriceComponent implements OnInit, OnDestroy {
  @Output() priceModified = new EventEmitter<XandrSegmentBilling>();
  publicSegment!: boolean;
  priceCategories!: XandrPricingDataCategory[];
  types = Object.values(XandrBillingType);
  selectedPriceCategory!: XandrPricingDataCategory;
  selectedType!: XandrBillingType;
  loading = false;
  private accountRaw!: DistributionAccount;
  private subscriptionHandler = new SubscriptionHandler();

  constructor(public xandrService: XandrService,
              private notificationService: NotificationService,
              private dialog: MatDialog) {
  }

  @Input()
  get account(): DistributionAccount {
    return this.accountRaw;
  }

  set account(account: DistributionAccount) {
    if (!this.account && account && this.xandrService.isDryMode) {
      this.accountRaw = account;
      if (account) {
        this.loadData();
      }
    }
    this.accountRaw = account;
  }

  private _billing!: XandrSegmentBilling;

  @Input()
  get billing(): XandrSegmentBilling {
    return this._billing;
  }

  set billing(billing: XandrSegmentBilling) {
    this._billing = billing;
    if (this.account) {
      this.loadData();
    }
  }

  ngOnInit(): void {

    if (this.xandrService.isDryMode) {
      // preselect private when in dryMode
      setTimeout(() => {
        this.publicSegment = false;
        this.emitPrice();
      }, 1000);

    }

  }

  ngOnDestroy() {
    this.subscriptionHandler.unsubscribe();
  }

  updatePrice() {
    this.loading = true;

    this.subscriptionHandler.subscription = this.xandrService.updateSegmentBillingMapping(
      this.billing.segmentId.toString(),
      this.selectedPriceCategory.id,
      this.selectedType,
      this.account.apiId
    )
      .subscribe({
        next: () => {
          this.loading = false;
          this.notificationService.success(`Updated ${this.publicSegment ? 'public' : 'private'} xandr price`);
          this.emitPrice();
        },
        error: () => {
          this.loading = false;
          this.notificationService.error(`Error updating ${this.publicSegment ? 'public' : 'private'} xandr price`);
        }
      });
  }

  createPrice() {
    this.loading = true;
    this.subscriptionHandler.subscription = this.xandrService.createSegmentBillingMapping(
      this.account?.foreignIdForCurrentSegment,
      this.selectedPriceCategory.id,
      this.selectedType,
      this.publicSegment,
      true,
      this.account?.apiId
    )
      .subscribe({
        next: () => {
          this.loading = false;
          this.notificationService.success(`Created ${this.publicSegment ? 'public' : 'private'} xandr price`);
          this.emitPrice();
        },
        error: () => {
          this.loading = false;
          this.notificationService.error(`Error creating ${this.publicSegment ? 'public' : 'private'} xandr price`);
        }
      });
  }

  onChangePrice() {
    if (this.xandrService.isDryMode) {
      this.emitPrice();
    }
  }

  emitPrice() {
    this.priceModified.emit({
      active: true,
      dataCategoryId: this.selectedPriceCategory.id,
      dataSegmentTypeId: this.selectedType,
      id: 0,
      segmentId: 0,
      public: this.publicSegment,
      dataCategoryXandr: this.selectedPriceCategory
    });
  }

  btnUpdateEnabled() {
    return !this.createMode() && this.selectedPriceCategory && this.selectedType && (this.publicSegment != null);
  }

  btnCreateEnabled() {
    return this.createMode() && this.selectedPriceCategory && this.selectedType && (this.publicSegment != null);
  }

  deleteBillingMapping() {
    const dialogData: ConfirmDialogModel = {
      title: 'Xandr Segment Billing Mapping',
      message: 'Are you sure you want to delete the segment billing mapping?'
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {maxWidth: '400px', data: dialogData});
    this.subscriptionHandler.subscription = dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.loading = true;
        this.xandrService.deleteSegmentBillingMapping(String(this.billing.segmentId), this.account.apiId)
          .subscribe({
            next: () => {
              this.loading = false;
              this.notificationService.success(`Deleted ${this.publicSegment ? 'public' : 'private'} xandr price`);
              this.emitPrice();
            },
            error: () => {
              this.loading = false;
              this.notificationService.success(`Error deleting ${this.publicSegment ? 'public' : 'private'} xandr price`);
            }
          });
      }
    });
  }

  createMode(): boolean {
    return !(this.billing) || this.xandrService.isDryMode;
  }

  title(): string {
    if (!(this.billing)) {
      return this.xandrService.isDryMode ? 'Price' : 'Price not set';
    }

    return this.billing.public ? 'Public price' : 'Private price';
  }

  private loadData() {
    this.subscriptionHandler.subscription = this.xandrService.getBillingCategories(this.account?.apiId)
      .subscribe({
        next: (priceCategories) => {
          this.priceCategories = priceCategories;
          this.selectedPriceCategory = this.priceCategories
            .find(cat => cat.id === this.billing?.dataCategoryXandr?.id) || this.priceCategories[0];
          this.selectedType = this.billing?.dataSegmentTypeId as XandrBillingType || XandrBillingType.Interest;
          this.publicSegment = this.billing?.public;
        },
        error: (error) => {
          console.error('Error', error);
        }
      });
  }
}
